import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthState, LoginCredentials } from '../types/auth';
import { loginUser, verifyToken } from '../services/authService';

// Definición de roles como un tipo enum para mayor seguridad
export type UserRole = 'admin' | 'programer' | 'medico' | 'recepcion' | 'bioanalista' | 'impresion';

// Extender la interfaz AuthState para incluir el estado de carga
interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  checkPermission: (requiredRoles: string[]) => boolean;
  loading: boolean;
  selectTenant: (tenantId: string) => void;
  needsTenantSelection: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Estado inicial con loading: true para evitar redirecciones prematuras
  const [auth, setAuth] = useState<AuthState & { loading: boolean }>({
    user: null,
    token: localStorage.getItem('token'),
    isAuthenticated: false,
    loading: true,
    tenants: [],
    selectedTenant: null
  });


  // Verificación de token al montar el componente con manejo mejorado
  useEffect(() => {
    const verifyAuth = async () => {

      const token = localStorage.getItem('token');

      if (!token) {

        setAuth({
          user: null,
          token: null,
          isAuthenticated: false,
          loading: false,
          tenants: [],
          selectedTenant: null
        });
        return;
      }

      try {
        const user = await verifyToken(token);

        // Restore selectedTenant and tenants from localStorage
        const savedTenantStr = localStorage.getItem('selectedTenant');
        const savedTenantsStr = localStorage.getItem('tenants');
        const selectedTenant = savedTenantStr ? JSON.parse(savedTenantStr) : null;
        const tenants = savedTenantsStr ? JSON.parse(savedTenantsStr) : [];

        setAuth({
          user,
          token,
          isAuthenticated: true,
          loading: false,
          tenants,
          selectedTenant
        });
      } catch (error) {

        // Solo eliminar el token si es un error de autenticación (no de red)
        const err = error as any;
        if (err.message && (
          err.message.includes('Token inválido') ||
          err.message.includes('expirado') ||
          err.message.includes('401'))
        ) {
          localStorage.removeItem('token');
        }

        setAuth({
          user: null,
          token: null,
          isAuthenticated: false,
          loading: false,
          tenants: [],
          selectedTenant: null
        });
      }
    };

    verifyAuth();

    // Verificar el token periódicamente (cada 10 minutos)
    const intervalId = setInterval(verifyAuth, 10 * 60 * 1000);

    // Limpiar el intervalo cuando el componente se desmonte
    return () => clearInterval(intervalId);
  }, []);

  // También verificar cuando la ventana recupera el foco
  useEffect(() => {
    const handleFocus = () => {
      const token = localStorage.getItem('token');
      if (token && !auth.loading && !auth.isAuthenticated) {
        verifyToken(token)
          .then(user => {
            const savedTenantStr = localStorage.getItem('selectedTenant');
            const savedTenantsStr = localStorage.getItem('tenants');
            const selectedTenant = savedTenantStr ? JSON.parse(savedTenantStr) : null;
            const tenants = savedTenantsStr ? JSON.parse(savedTenantsStr) : [];

            setAuth({
              user,
              token,
              isAuthenticated: true,
              loading: false,
              tenants,
              selectedTenant
            });
          })
          .catch(error => console.error('Error al verificar token en focus:', error));
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [auth.loading, auth.isAuthenticated]);

  // Método de login con manejo de errores mejorado
  const login = async (credentials: LoginCredentials) => {
    try {
      const responseData = await loginUser(credentials);
      const { token, user, tenants } = responseData;

      // Almacenar token en localStorage
      localStorage.setItem('token', token);

      // Store tenants if present (programer/system users)
      if (tenants && tenants.length > 0) {
        localStorage.setItem('tenants', JSON.stringify(tenants));
      }

      // Actualizar estado
      setAuth({
        user,
        token,
        isAuthenticated: true,
        loading: false,
        tenants: tenants || [],
        selectedTenant: null
      });
      return { success: true };
    } catch (error: any) {
      // Extraer mensaje del backend si existe
      let message = 'Credenciales inválidas';
      if (error.response && error.response.data && error.response.data.message) {
        message = error.response.data.message;
      }
      return { success: false, message };
    }
  };

  // Método de logout
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tenants');
    localStorage.removeItem('selectedTenant');
    localStorage.removeItem('tenantId');
    setAuth({
      user: null,
      token: null,
      isAuthenticated: false,
      loading: false,
      tenants: [],
      selectedTenant: null
    });
  };

  // Método para seleccionar un tenant (programer users)
  const selectTenant = useCallback((tenantId: string) => {
    setAuth(prev => {
      const tenant = prev.tenants.find(t => t.tenantId === tenantId);
      if (tenant) {
        // Save full tenant object for display
        localStorage.setItem('selectedTenant', JSON.stringify(tenant));
        // Save tenantId for httpClient (reads tenantId from localStorage)
        localStorage.setItem('tenantId', tenant.tenantId);
        return { ...prev, selectedTenant: tenant };
      }
      return prev;
    });
  }, []);

  // Computed: does the current user (programer) need to select a tenant?
  const needsTenantSelection = !!(auth.user?.isSystemUser && auth.user?.role === 'programer' && auth.tenants.length > 0 && !auth.selectedTenant);

  // Método de verificación de permisos con depuración
  const checkPermission = (requiredRoles: string[]) => {
    // Sin usuario autenticado, no hay permisos
    if (!auth.user) {
      return false;
    }

    // Admin siempre tiene acceso
    if (auth.user.role === 'admin') {

      return true;
    }

    // Verificar si el rol del usuario está en los roles requeridos
    const hasPermission = requiredRoles.includes(auth.user.role);


    return hasPermission;
  };

  return (
    <AuthContext.Provider value={{
      ...auth,
      login,
      logout,
      checkPermission,
      selectTenant,
      needsTenantSelection
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};